/**
 * Axios API wrapper with JWT interceptors.
 *
 * Reads the auth token from localStorage on every request (avoiding
 * circular dependency with the React auth hook).  On 401, clears
 * stored credentials and redirects to /login.
 *
 * Every backend endpoint has a named convenience function that
 * returns the unwrapped JSON payload (or Blob for export endpoints).
 */
import axios from 'axios';

import { clearComparisonCache } from './comparisonCache';

// ---------------------------------------------------------------------------
// localStorage keys (shared with useAuth)
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const api = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach Bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — handle 401 (expired / invalid token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Redirect unless already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** POST /api/auth/login */
export async function loginApi(username, password, rememberMe = false) {
  const { data } = await api.post('/api/auth/login', {
    username,
    password,
    remember_me: rememberMe,
  });
  return data;
}

/** POST /api/auth/refresh */
export async function refreshToken() {
  const { data } = await api.post('/api/auth/refresh');
  return data;
}

/** GET /api/auth/me */
export async function getMeApi() {
  const { data } = await api.get('/api/auth/me');
  return data;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/** GET /api/dashboard/metrics?period=... */
export async function getMetrics(period = '1w') {
  const { data } = await api.get('/api/dashboard/metrics', {
    params: { period },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Comparison
// ---------------------------------------------------------------------------

/** GET /api/comparison?limit=...&competitors=...&... */
export async function getComparison(params = {}, signal) {
  const config = { params };
  if (signal) config.signal = signal;
  const { data } = await api.get('/api/comparison', config);
  return data;
}

// ---------------------------------------------------------------------------
// Trends
// ---------------------------------------------------------------------------

/** GET /api/trends/{sku}?period=...&competitors=... */
export async function getTrend(sku, params = {}) {
  const { data } = await api.get(
    `/api/trends/${encodeURIComponent(sku)}`,
    { params },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Changelog
// ---------------------------------------------------------------------------

/** GET /api/changelog?page=...&per_page=...&domain=...&sku=...&period=... */
export async function getChangelog(params = {}) {
  const { data } = await api.get('/api/changelog', { params });
  return data;
}

// ---------------------------------------------------------------------------
// Quality
// ---------------------------------------------------------------------------

/** GET /api/quality/summary?period=... */
export async function getQualitySummary(params = {}) {
  const { data } = await api.get('/api/quality/summary', { params });
  return data;
}

/** GET /api/quality/domain/{domainId}?period=... */
export async function getQualityDetail(domainId, params = {}) {
  const { data } = await api.get(
    `/api/quality/domain/${encodeURIComponent(domainId)}`,
    { params },
  );
  return data;
}

/** GET /api/quality/run-status */
export async function getRunStatus() {
  const { data } = await api.get('/api/quality/run-status');
  return data;
}

// ---------------------------------------------------------------------------
// Freshness
// ---------------------------------------------------------------------------

/** GET /api/freshness?since=... */
export async function getFreshness(params = {}) {
  const { data } = await api.get('/api/freshness', { params });
  return data;
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

/** GET /api/admin/users */
export async function getUsers() {
  const { data } = await api.get('/api/admin/users');
  return data;
}

/** POST /api/admin/users */
export async function createUser(userData) {
  const { data } = await api.post('/api/admin/users', userData);
  return data;
}

/** DELETE /api/admin/users/{username} */
export async function deleteUser(username) {
  const { data } = await api.delete(
    `/api/admin/users/${encodeURIComponent(username)}`,
  );
  return data;
}

/** GET /api/admin/archived-products */
export async function getArchivedProducts() {
  const { data } = await api.get('/api/admin/archived-products');
  return data;
}

/** POST /api/admin/archived-products */
export async function archiveProduct(ourSku, reason) {
  const { data } = await api.post('/api/admin/archived-products', {
    our_sku: ourSku,
    reason: reason || null,
  });
  return data;
}

/** DELETE /api/admin/archived-products/{our_sku} */
export async function unarchiveProduct(ourSku) {
  const { data } = await api.delete(
    `/api/admin/archived-products/${encodeURIComponent(ourSku)}`,
  );
  return data;
}

/** PUT /api/admin/users/{username}/role */
export async function changeUserRole(username, role) {
  const { data } = await api.put(
    `/api/admin/users/${encodeURIComponent(username)}/role`,
    { role },
  );
  return data;
}

/** POST /api/admin/rebuild-database?data_dir=... */
export async function rebuildDatabase(dataDir = 'data/price-data') {
  const { data } = await api.post('/api/admin/rebuild-database', null, {
    params: { data_dir: dataDir },
  });
  return data;
}

/** POST /api/admin/backup-database */
export async function backupDatabase() {
  const { data } = await api.post('/api/admin/backup-database');
  return data;
}

/** POST /api/admin/refresh-data */
export async function refreshData() {
  const { data } = await api.post('/api/admin/refresh-data');
  // New data may have been ingested — drop the comparison cache so the
  // Comparison tab fetches fresh rows on its next mount instead of showing
  // stale cached data.
  clearComparisonCache();
  return data;
}

// ---------------------------------------------------------------------------
// Our-price feed snapshots (any logged-in user)
// ---------------------------------------------------------------------------

/**
 * POST /api/our-prices/refresh — fetch the store feed now and, if it has
 * been regenerated since the snapshot held, snapshot + ingest it.
 *
 * Resolves to `{ status, generated_at, previous_generated_at, row_count,
 * uploaded, message }` where status is one of `updated`,
 * `not_regenerated`, `busy`, `throttled`, `error`.
 */
export async function refreshOurPrices() {
  const { data } = await api.post('/api/our-prices/refresh');
  if (data.status === 'updated') {
    // Our prices changed — the comparison rows are stale.
    clearComparisonCache();
  }
  return data;
}

/** GET /api/our-prices/status */
export async function getOurPricesStatus() {
  const { data } = await api.get('/api/our-prices/status');
  return data;
}

/** GET /api/admin/ingestion-status */
export async function getIngestionStatus() {
  const { data } = await api.get('/api/admin/ingestion-status');
  return data;
}

/** GET /api/admin/settings */
export async function getAdminSettings() {
  const { data } = await api.get('/api/admin/settings');
  return data;
}

/** PUT /api/admin/settings */
export async function updateAdminSettings(settings) {
  const { data } = await api.put('/api/admin/settings', settings);
  return data;
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

/** GET /api/reports/list */
export async function listReports() {
  const { data } = await api.get('/api/reports/list');
  return data;
}

/** POST /api/reports/generate-weekly?date=... */
export async function generateReport(date = null) {
  const params = date ? { date } : {};
  const { data } = await api.post('/api/reports/generate-weekly', null, {
    params,
  });
  return data;
}

/** GET /api/reports/weekly?date=... — returns Blob (PDF) */
export async function downloadReport(date) {
  const { data } = await api.get('/api/reports/weekly', {
    params: { date },
    responseType: 'blob',
  });
  return data;
}

/** GET /api/reports/run-report/latest — metadata + sponsor summary md */
export async function getRunReportLatest() {
  const { data } = await api.get('/api/reports/run-report/latest');
  return data;
}

/** GET /api/reports/run-report/full — the complete report md */
export async function getRunReportFull() {
  const { data } = await api.get('/api/reports/run-report/full');
  return data;
}

/** GET /api/reports/run-report/download — returns Blob (markdown) */
export async function downloadRunReport() {
  const { data } = await api.get('/api/reports/run-report/download', {
    responseType: 'blob',
  });
  return data;
}

// ---------------------------------------------------------------------------
// CSV / PDF Export (return Blob for browser download)
// ---------------------------------------------------------------------------

/** GET /api/export/comparison?... */
export async function exportComparison(params = {}) {
  const { data } = await api.get('/api/export/comparison', {
    params,
    responseType: 'blob',
  });
  return data;
}

/** GET /api/export/kpi?category=...&period=... */
export async function exportKpi(category, period) {
  const { data } = await api.get('/api/export/kpi', {
    params: { category, period },
    responseType: 'blob',
  });
  return data;
}

/** GET /api/export/trend/{sku}?... */
export async function exportTrend(sku, params = {}) {
  const { data } = await api.get(
    `/api/export/trend/${encodeURIComponent(sku)}`,
    { params, responseType: 'blob' },
  );
  return data;
}

/** GET /api/export/changelog?... */
export async function exportChangelog(params = {}) {
  const { data } = await api.get('/api/export/changelog', {
    params,
    responseType: 'blob',
  });
  return data;
}

/** GET /api/export/quality?... */
export async function exportQuality(params = {}) {
  const { data } = await api.get('/api/export/quality', {
    params,
    responseType: 'blob',
  });
  return data;
}

/** GET /api/export/freshness?... */
export async function exportFreshness(params = {}) {
  const { data } = await api.get('/api/export/freshness', {
    params,
    responseType: 'blob',
  });
  return data;
}

// ---------------------------------------------------------------------------
// Upload (master xlsx)
// ---------------------------------------------------------------------------

/**
 * POST /api/upload/master — stage a master xlsx for review.
 *
 * The instance default Content-Type is application/json, so it must be
 * overridden here: axios then replaces it with the browser's
 * multipart/form-data value carrying the form boundary.
 *
 * @param {File} file                  The .xlsx file to upload.
 * @param {Function} [onProgress]      Axios onUploadProgress callback.
 */
export async function uploadMasterXlsx(file, onProgress) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/api/upload/master', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
  return data;
}

/** POST /api/upload/master/{id}/confirm?ack_scrape_window=... */
export async function confirmMasterUpload(uploadId, ackScrapeWindow = false) {
  const { data } = await api.post(
    `/api/upload/master/${encodeURIComponent(uploadId)}/confirm`,
    null,
    { params: { ack_scrape_window: ackScrapeWindow } },
  );
  return data;
}

/** DELETE /api/upload/master/{id} */
export async function cancelMasterUpload(uploadId) {
  const { data } = await api.delete(
    `/api/upload/master/${encodeURIComponent(uploadId)}`,
  );
  return data;
}

/** GET /api/upload/history?limit=... */
export async function getUploadHistory(limit = 50) {
  const { data } = await api.get('/api/upload/history', {
    params: { limit },
  });
  return data;
}

/**
 * GET /api/upload/master/snapshots/{name} — returns Blob (xlsx).
 *
 * @param {string} name  Dated snapshot filename from a history row's
 *                       detail.retained_sheet.dated_name.
 */
export async function downloadMasterSnapshot(name) {
  const { data } = await api.get(
    `/api/upload/master/snapshots/${encodeURIComponent(name)}`,
    { responseType: 'blob' },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Default export — the raw Axios instance (for advanced usage)
// ---------------------------------------------------------------------------
export default api;
